import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FiRefreshCw, FiSave } from 'react-icons/fi';
import toast from 'react-hot-toast';
import WarningIcon from '../common/icons/WarningIcon';
import ArduinoIcon from '../common/icons/ArduinoIcon';
import HardwareChipIcon from '../common/icons/HardwareChipIcon';
import GamepadIcon from '../common/icons/GamepadIcon';
import { db } from '../../services/supabase';

const formatDateTimeLocal = (value) => {
  if (!value) return '';
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (num) => String(num).padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch (error) {
    return '';
  }
};

const SettingsForm = () => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    event_name: '',
    ticket_price: '',
    max_tickets: '',
    sales_start: '',
    sales_end: '',
  });
  
  // Arduino connection state
  const [arduinoConnected, setArduinoConnected] = useState(false);
  const [serialPort, setSerialPort] = useState(null);

  const { data: eventSettings, isLoading, error, refetch } = useQuery({
    queryKey: ['event-settings'],
    queryFn: db.eventSettings.getLatest,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!eventSettings) return;
    setForm({
      event_name: eventSettings.event_name ?? '',
      ticket_price:
        eventSettings.ticket_price !== null && eventSettings.ticket_price !== undefined
          ? String(eventSettings.ticket_price)
          : '',
      max_tickets:
        eventSettings.max_tickets !== null && eventSettings.max_tickets !== undefined
          ? String(eventSettings.max_tickets)
          : '',
      sales_start: formatDateTimeLocal(eventSettings.sales_start),
      sales_end: formatDateTimeLocal(eventSettings.sales_end),
    });
  }, [eventSettings]);

  const updateMutation = useMutation({
    mutationFn: async (payload) => db.eventSettings.update(eventSettings.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-settings'] });
      toast.success('Nastavenia boli aktualizované.');
    },
    onError: (mutationError) => {
      toast.error(`Nepodarilo sa uložiť nastavenia: ${mutationError.message}`);
    },
  });

  const isSalesActive = useMemo(() => {
    if (!eventSettings?.sales_start || !eventSettings?.sales_end) return false;
    const now = new Date();
    return now >= new Date(eventSettings.sales_start) && now <= new Date(eventSettings.sales_end);
  }, [eventSettings]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!eventSettings?.id) {
      toast.error('Nie je k dispozícii žiadny záznam nastavení na úpravu.');
      return;
    }

    const normalizedMaxTickets = Number(form.max_tickets) || 0;
    if (normalizedMaxTickets > 1000) {
      toast.error('Maximálny počet lístkov je 1000 (sekcie A-E, 200 na sekciu).');
      return;
    }

    updateMutation.mutate({
      event_name: form.event_name,
      ticket_price: Number(form.ticket_price) || 0,
      max_tickets: normalizedMaxTickets,
      sales_start: form.sales_start ? new Date(form.sales_start).toISOString() : null,
      sales_end: form.sales_end ? new Date(form.sales_end).toISOString() : null,
    });
  };

  // Arduino connection functions
  const connectArduino = async () => {
    if (!('serial' in navigator)) {
      toast.error('Web Serial API nie je podporované v tomto prehliadači');
      return;
    }

    try {
      const port = await navigator.serial.requestPort();
      await port.open({ baudRate: 9600 });
      setSerialPort(port);
      setArduinoConnected(true);
      
      // Listen for Arduino signals
      const reader = port.readable.getReader();
      readArduinoData(reader);
      
      toast.success('Arduino pripojené!', {
        icon: <HardwareChipIcon className="text-success-400" aria-hidden="true" />,
      });
    } catch (error) {
      if (error.name !== 'NotFoundError') {
        toast.error('Chyba pri pripájaní Arduino');
        console.error('Arduino connection error:', error);
      }
    }
  };

  const readArduinoData = async (reader) => {
    let keepReading = true;
    try {
      while (keepReading) {
        const { value, done } = await reader.read();
        if (done) {
          keepReading = false;
          continue;
        }

        const text = new TextDecoder().decode(value);
        if (text.includes('BUTTON_PRESSED')) {
          // Dispatch custom event for DrawInterface to listen
          window.dispatchEvent(new CustomEvent('arduino:button-pressed'));
        }
      }
    } catch (error) {
      console.error('Arduino read error:', error);
      setArduinoConnected(false);
    }
  };

  const disconnectArduino = async () => {
    if (serialPort) {
      try {
        await serialPort.close();
        setSerialPort(null);
        setArduinoConnected(false);
        toast.success('Arduino odpojené', {
          icon: <HardwareChipIcon className="text-warning-400" aria-hidden="true" />,
        });
      } catch (error) {
        console.error('Error disconnecting Arduino:', error);
      }
    }
  };

  const simulateArduino = () => {
    // Dispatch custom event for DrawInterface to listen
    window.dispatchEvent(new CustomEvent('arduino:button-pressed'));
    toast.success('Simulácia Arduino signálu odoslaná', {
      icon: <GamepadIcon className="text-primary-400" aria-hidden="true" />,
    });
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (serialPort) {
        serialPort.close().catch(console.error);
      }
    };
  }, [serialPort]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center text-slate-200">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-400 mx-auto mb-4" />
          <p className="text-lg text-slate-300">Načítavam nastavenia...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-12">
        <div className="text-error-400 text-6xl mb-4 flex justify-center">
          <WarningIcon className="text-5xl" aria-hidden="true" />
        </div>
        <h3 className="text-xl font-semibold text-primary-100 mb-4">Chyba pri načítaní nastavení</h3>
        <button onClick={() => refetch()} className="btn btn-primary">
          <FiRefreshCw className="w-4 h-4 mr-2" />
          Skúsiť znovu
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="card backdrop-blur-sm border border-slate-800/60 bg-slate-900/60">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-primary-100">Nastavenia tomboly</h2>
            <p className="text-sm text-slate-400">
              Aktualizujte základné parametre predaja lístkov.
            </p>
          </div>
          <div
            className={`px-3 py-1 rounded-full text-xs font-medium border ${
              isSalesActive
                ? 'bg-success-500/15 text-success-200 border-success-500/40'
                : 'bg-warning-500/15 text-warning-200 border-warning-500/40'
            }`}
          >
            {isSalesActive ? 'Predaj aktívny' : 'Predaj pozastavený'}
          </div>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="card backdrop-blur-sm border border-slate-800/60 bg-slate-900/60 space-y-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Názov podujatia
            </label>
            <input
              type="text"
              name="event_name"
              value={form.event_name}
              onChange={handleChange}
              className="w-full bg-slate-950/80 border border-slate-800 rounded-xl px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Cena lístka (EUR)
            </label>
            <input
              type="number"
              name="ticket_price"
              min="0"
              step="0.5"
              value={form.ticket_price}
              onChange={handleChange}
              className="w-full bg-slate-950/80 border border-slate-800 rounded-xl px-4 py-3 text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Maximálny počet lístkov
            </label>
            <input
              type="number"
              name="max_tickets"
              min="1"
              max="1000"
              step="1"
              value={form.max_tickets}
              onChange={handleChange}
              className="w-full bg-slate-950/80 border border-slate-800 rounded-xl px-4 py-3 text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
              required
            />
            <p className="mt-1 text-xs text-slate-400">Limit systému: 1000 lístkov (A-E, 200/sekcia).</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Začiatok predaja
            </label>
            <input
              type="datetime-local"
              name="sales_start"
              value={form.sales_start}
              onChange={handleChange}
              className="w-full bg-slate-950/80 border border-slate-800 rounded-xl px-4 py-3 text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Koniec predaja
            </label>
            <input
              type="datetime-local"
              name="sales_end"
              value={form.sales_end}
              onChange={handleChange}
              className="w-full bg-slate-950/80 border border-slate-800 rounded-xl px-4 py-3 text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
              required
            />
          </div>
        </div>

        <div className="flex justify-end gap-4 pt-4">
          <button
            type="button"
            onClick={() => refetch()}
            className="btn btn-secondary flex items-center gap-2"
          >
            <FiRefreshCw className="w-4 h-4" />
            Obnoviť
          </button>
          <button
            type="submit"
            className="btn btn-primary flex items-center gap-2"
            disabled={updateMutation.isPending}
          >
            <FiSave className="w-4 h-4" />
            Uložiť
          </button>
        </div>
      </form>

      {/* Arduino Connection */}
      <div className="card backdrop-blur-sm border border-slate-800/60 bg-slate-900/60">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-2xl font-semibold text-primary-100 mb-2 flex items-center gap-2">
              <ArduinoIcon className="text-success-300" aria-hidden="true" />
              <span>Arduino ovládanie</span>
            </h2>
            <p className="text-sm text-slate-400">
              Pripojte Arduino pre automatické žrebovanie cez hardvérové tlačidlo.
            </p>
          </div>
        </div>
        
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-4 h-4 rounded-full border border-slate-700 ${arduinoConnected ? 'bg-success-400 animate-pulse shadow-glow' : 'bg-slate-700'}`}></div>
            <span className="text-slate-300">
              {arduinoConnected ? 'Arduino pripojené' : 'Arduino nepripojené'}
            </span>
          </div>
          
          <div className="flex flex-wrap gap-3">
            {!arduinoConnected ? (
              <button onClick={connectArduino} className="btn btn-primary">
                Pripojiť Arduino
              </button>
            ) : (
              <button onClick={disconnectArduino} className="btn btn-secondary">
                Odpojiť Arduino
              </button>
            )}
            
            <button
              onClick={simulateArduino}
              className="btn btn-secondary flex items-center gap-2"
            >
              <GamepadIcon aria-hidden="true" />
              <span>Simulovať tlačidlo</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsForm;
